import React from 'react';
import Slider from 'react-slick';
import { useLocation } from '@reach/router';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { useCountry } from '@hooks/useCountry';
import ArrowNext from '@images/slider-arrow-next.svg';
import ArrowPrev from '@images/slider-arrow-prev.svg';
import { replaceText } from '@utils/handleTextReplacement';
import useDesktop from './useDesktop';

const NextArrow = (props: any) => {
  const { className, onClick } = props;
  return <ArrowNext onClick={onClick} className={className} />;
};

const PrevArrow = (props: any) => {
  const { className, onClick } = props;
  return <ArrowPrev onClick={onClick} className={className} />;
};

interface Props {
  slides: {
    image: { gatsbyImageData: IGatsbyImageData; alt: string };
    thumbnail: { gatsbyImageData: IGatsbyImageData; alt: string };
  }[];
  arrows: boolean;
  pagination: boolean;
  maxWidth: string;
}

const ImageSlider: React.FC<Props> = ({ slides, arrows, pagination, maxWidth }) => {
  const isDesktop = useDesktop(768);
  const location = useLocation();
  const { country } = useCountry();

  let settings = {
    dots: pagination,
    dotsClass: 'slick-dots slick-thumb',
    infinite: true,
    fade: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: arrows ? (
      <ArrowWrap>
        <NextArrow />
      </ArrowWrap>
    ) : null,
    prevArrow: arrows ? (
      <ArrowWrap>
        <PrevArrow />
      </ArrowWrap>
    ) : null
  };

  if (isDesktop) settings = { ...settings };

  return (
    <SliderWrap $maxWidth={maxWidth}>
      <Slider {...settings}>
        {slides?.map((slide: any, index: number) => {
          if (slide?.image) {
            return <GatsbyImage image={slide.image.gatsbyImageData} alt={replaceText(slide.image.alt, country, location) ?? ''} key={index} />;
          }
        })}
      </Slider>
    </SliderWrap>
  );
};

export default ImageSlider;

interface StyleProps {
  $maxWidth: string;
}

const SliderWrap = styled.div<StyleProps>`
  max-width: ${(props) => props.$maxWidth};
  width: 100%;

  .slick-track {
    display: flex;
    align-items: center;
  }

  .slick-dots {
    display: flex !important;
    flex-wrap: nowrap;
    justify-content: center;
    position: relative;
    bottom: 0;
    width: 80%;
    margin: 20px auto 0;
  }

  .slick-dots li {
    &:hover {
      opacity: 0.7;
    }

    & > a {
      display: inline-block;
      margin-bottom: 0;
      height: 100%;
      background: ${Colors.cream};
      border: 1px solid ${Colors.ecru};
      box-sizing: border-box;
      border-radius: 2px;

      & > div {
        height: 100%;
      }
    }
  }

  @media (max-width: 768px) {
    width: 100%;

    .slick-arrow {
      display: none;
    }

    .slick-dots li {
      height: 10px;
      width: 10px;
    }
  }
`;

const ArrowWrap = styled.div`
  width: 30px;
  height: 30px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex !important;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 12px;
    height: 22px;
  }

  &.slick-prev {
    left: -30px;
  }

  &.slick-next {
    right: -30px;
  }

  @media (max-width: 1024px) {
    display: none !important;
  }
`;
